import React from "react"
import { Block } from "baseui/block"
import AngleDoubleLeft from "~/components/Icons/AngleDoubleLeft"
import Scrollable from "~/components/Scrollable"
import { Button, SIZE } from "baseui/button"
import DropZone from "~/components/Dropzone"
import { useEditor } from "@layerhub-io/react"
import useSetIsSidebarOpen from "~/hooks/useSetIsSidebarOpen"
import { nanoid } from "nanoid"
import useDesignEditorContext from "~/hooks/useDesignEditorContext"
import useAppContext from "~/hooks/useAppContext"
import Loading from "~/components/Loading"
import Cookies from "js-cookie"
import { buildStampedName, listAllFiles, nextUploadStamp } from "~/services/supabase"


// Browsers only keep a handful of connections open per host anyway, so going wider than
// this just queues requests while making a failure harder to attribute.
const UPLOAD_CONCURRENCY = 6

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })

export default function () {
  const inputFileRef = React.useRef<HTMLInputElement>(null)
  const {uploads, setUploads} = useAppContext()
  const editor = useEditor()
  const setIsSidebarOpen = useSetIsSidebarOpen()
  const { currentDesign, setCurrentDesign } = useDesignEditorContext()
  const { supabase } = useAppContext()
  const { id } = currentDesign
  const [files, setFiles] = React.useState<FileList | null>(null);
  const [loading, setLoading] = React.useState(false);
  const premiumMode = Cookies.get('premiumMode') === 'true'
  const premiumDesignId = Cookies.get('premiumDesignId') || id

  const folderPath = `projects/${premiumMode ? premiumDesignId : id}`
  const baseStorageUrl = `https://igalgzepbejgggxvyder.supabase.co/storage/v1/object/public/images/${folderPath}/`
  const designIdentifier = premiumMode ? premiumDesignId : id

  const getStoredPremiumUploads = React.useCallback(() => {
    const rawValue = localStorage.getItem("premiumUploads")
    if (!rawValue) {
      return []
    }

    try {
      return JSON.parse(rawValue)
    } catch (error) {
      console.error("Error reading premium uploads", error)
      return []
    }
  }, [])

  //load uploads during initial render
  React.useEffect(() => {
    if (!designIdentifier) {
      console.log("Waiting for project to be initialized...")
      return
    }

    // Skip fetch if uploads are already loaded (e.g. navigating back to this panel)
    if (uploads.length > 0) return

    if (premiumMode) {
      const premiumUploads = getStoredPremiumUploads()
      if (premiumUploads.length > 0) {
        setUploads(premiumUploads)
        return
      }
    }

    setLoading(true)
    const fetchUploads = async () => {
      let data
      try {
        data = await listAllFiles(folderPath, supabase)
      } catch (error) {
        // Handle error
        console.error("Error listing images",error)
        setLoading(false)
        return
      }

      let imageUrls = []

      data.forEach((image) => {
        //prepend baseStorageUrl to image name
        image.url = baseStorageUrl + image.name
        imageUrls.push({
          id: nanoid(),
          src: image.url,
          preview: image.url,
          type: "StaticImage",
        })
      })

      
      setUploads(imageUrls)
      setLoading(false)
    }
    fetchUploads()
  }, [designIdentifier, folderPath, getStoredPremiumUploads, premiumMode, setUploads, supabase, uploads.length])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFiles(event.target.files);
  };

  // Uploads run in bounded waves; firing hundreds of requests at once is where large
  // batches start dropping files. Order does not depend on which upload finishes first
  // — the stamped name carries it.
  const uploadInWaves = async (entries: { file: File; storedName: string }[]) => {
    const storedNames: string[] = []

    for (let i = 0; i < entries.length; i += UPLOAD_CONCURRENCY) {
      const wave = entries.slice(i, i + UPLOAD_CONCURRENCY)
      const results = await Promise.all(
        wave.map(async ({ file, storedName }) => {
          const { error } = await supabase.storage.from('images').upload(`${folderPath}/${storedName}`, file)

          if (error) {
            console.error("Error uploading", storedName, error)
            return null
          }

          return storedName
        })
      )

      results.forEach((storedName) => {
        if (storedName) {
          storedNames.push(storedName)
        }
      })
    }

    return storedNames
  }

  // Shared by the file picker and by drag-and-drop, so dropped files reach storage
  // instead of only showing in the panel until the next reload.
  const addFiles = async (files: FileList | null) => {
    if (!designIdentifier) {
      console.error("Cannot upload: Project not initialized yet")
      return
    }

    if (!files || files.length === 0) {
      return
    }

    setLoading(true)

    try {
      const existing = await listAllFiles(folderPath, supabase)
      let stamp = nextUploadStamp(existing)

      const entries = Array.from(files).map((file) => ({
        file,
        storedName: buildStampedName(stamp++, file.name),
      }))

      const storedNames = await uploadInWaves(entries)

      // Only files that actually reached storage belong in the panel — anything else
      // would show here and then disappear on the next reload.
      const newUploads = storedNames.map((storedName) => {
        const url = baseStorageUrl + storedName

        return {
          id: nanoid(),
          src: url,
          preview: url,
          type: "StaticImage",
        }
      })

      setUploads((previous) => [...previous, ...newUploads])
    } catch (error) {
      console.error("Error adding files", error)
    } finally {
      setLoading(false)
    }
  }

  const handleDragStart = (event) => {
    event.dataTransfer.setData("text/plain", event.target.src);
  };
  
  const handleInputFileRefClick = () => {
    inputFileRef.current?.click()
  }

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await addFiles(e.target.files)
    // Clear the picker so the same selection can be chosen again after a failed upload.
    e.target.value = ""
  }

  const addImageToCanvas = (props) => {
    return
    editor.objects.add(props)
  }
  return (
    <DropZone handleDropFiles={addFiles}>
      <Block $style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Block
          $style={{
            display: "flex",
            alignItems: "center",
            fontWeight: 500,
            justifyContent: "space-between",
            padding: "1.5rem",
          }}
        >
          <Block>Uploads</Block>

          <Block onClick={() => setIsSidebarOpen(false)} $style={{ cursor: "pointer", display: "flex" }}>
            <AngleDoubleLeft size={18} />
          </Block>
        </Block>
        <Scrollable>
          <Block padding={"0 1.5rem"}>
            <Button
              onClick={handleInputFileRefClick}
              size={SIZE.compact}
              overrides={{
                Root: {
                  style: {
                    width: "100%",
                  },
                },
              }}
            >
              Computer
            </Button>
            <input multiple onChange={handleFileInput} type="file" id="file" ref={inputFileRef} style={{ display: "none" }} />

            <div
              style={{
                marginTop: "1rem",
                display: "grid",
                gap: "0.5rem",
                gridTemplateColumns: "1fr 1fr",
              }}
            >
              {uploads.map((upload) => (
                <div
                  key={upload.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => addImageToCanvas(upload)}
                >
                  <div>
                    <img draggable='true' width="100%" src={upload.preview ? upload.preview : upload.url} onDragStart={handleDragStart} alt="preview" loading="lazy" />
                  </div>
                </div>
              ))}
              {loading && <Loading />}
            </div>
          </Block>
        </Scrollable>
      </Block>
    </DropZone>
  )
}
